/**
 * Image optimization utilities for Unsplash-hosted images.
 *
 * Unsplash supports query parameters for resizing, quality and format conversion.
 * By appending the right params we deliver smaller, modern-format (WebP) images
 * sized to the actual rendered dimensions, which dramatically reduces LCP and
 * total page weight without changing visuals.
 *
 * For non-Unsplash URLs (or already-data URIs) we return the input unchanged.
 */

const isUnsplash = (url: string): boolean =>
  typeof url === "string" && url.includes("images.unsplash.com");

const stripExisting = (url: string, params: string[]): string => {
  try {
    const u = new URL(url);
    params.forEach((p) => u.searchParams.delete(p));
    return u.toString();
  } catch {
    return url;
  }
};

/**
 * Build an optimized Unsplash URL at a specific width.
 * Forces WebP, q=75 (visually indistinguishable, big savings), and DPR-aware width.
 */
export const optimizedImage = (
  url: string | undefined,
  width: number,
  opts: { quality?: number; format?: "webp" | "auto" } = {}
): string | undefined => {
  if (!url) return url;
  if (!isUnsplash(url)) return url;

  const { quality = 75, format = "webp" } = opts;
  const cleaned = stripExisting(url, ["w", "q", "fm", "auto", "h", "fit"]);

  try {
    const u = new URL(cleaned);
    u.searchParams.set("w", String(Math.round(width)));
    u.searchParams.set("q", String(quality));
    if (format === "webp") {
      u.searchParams.set("fm", "webp");
    } else {
      u.searchParams.set("auto", "format");
    }
    u.searchParams.set("fit", "crop");
    return u.toString();
  } catch {
    return url;
  }
};

/**
 * Build a srcSet string for responsive images at 1x and 2x DPR.
 */
export const optimizedSrcSet = (
  url: string | undefined,
  width: number,
  opts: { quality?: number } = {}
): string | undefined => {
  if (!url || !isUnsplash(url)) return undefined;
  const x1 = optimizedImage(url, width, opts);
  const x2 = optimizedImage(url, width * 2, opts);
  if (!x1 || !x2) return undefined;
  return `${x1} 1x, ${x2} 2x`;
};
